import React, { useEffect, useState } from "react";
import { Card, Col, Placeholder, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import TruncatedText from "../../../../components/TruncatedText/TruncatedText";
import { useSession } from "../../../../contexts/SessionContext";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { DemoRecord } from "./LatestDemo.d";
import { fetchLatestDemo } from "./LatestDemo.function";

const LatestDemo = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [latestDemo, setLastestDemo] = useState<DemoRecord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLatestDemo(setLastestDemo, setLoading, language);
  }, [language]);

  return (
    <Card className="shadow-sm mx-auto m-regular">
      <Card.Body className="p-4">
        {loading ? (
          <React.Fragment>
            <Placeholder as="div" animation="glow" className="mb-3">
              <Placeholder xs={10} className="mb-2" />

              <Row>
                <Col>
                  <Placeholder xs={6} className="mb-2" />
                  <Placeholder xs={8} className="mb-2" />
                </Col>
                <Col className="d-flex justify-content-end align-items-start">
                  <Placeholder xs={1} />
                </Col>
              </Row>
            </Placeholder>
            <Row>
              <Col>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={6} /> <Placeholder xs={8} />
                </Placeholder>
              </Col>
              <Col>
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={5} /> <Placeholder xs={7} />
                </Placeholder>
              </Col>
            </Row>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="mb-2 fw-bold s-regular">{translate(language, "PAGES.HOME.LATEST_DEMO.DESCRIPTION")} </div>
            <div className="d-flex align-items-start justify-content-between mb-3">
              <div>
                <div className="text-uppercase text-muted mb-1 xs-regular">
                  {translate(language, "PAGES.HOME.LATEST_DEMO.TITLE")}
                </div>
                <Card.Title className="mb-0 h3">{latestDemo?.name}</Card.Title>
              </div>
              <span className="badge black-font off-white-background xxs-regular border">#{latestDemo?.id}</span>
            </div>
            {latestDemo?.description && (
              <div className="mt-3" id="latest-demo-description">
                <TruncatedText text={latestDemo.description} />
              </div>
            )}
            <Row className="mt-3">
              <Col>
                <div className="text-muted">{translate(language, "PAGES.HOME.LATEST_DEMO.AUTHOR")}</div>
                <div>
                  <Link to={`/user/${latestDemo?.author.id}`} className="link-underline link-underline-opacity-0">
                    {latestDemo?.author.full_name}
                  </Link>
                </div>
              </Col>
              <Col>
                <div className="text-muted">{translate(language, "PAGES.HOME.LATEST_DEMO.CREATED_AT")}</div>
                <div>{formatDateHumanly(language, latestDemo!.dt_created)}</div>
              </Col>
            </Row>
            {latestDemo?.documents_link || latestDemo?.record_link || latestDemo?.slide_link ? (
              <React.Fragment>
                <hr className="my-4" />
                <div className="text-muted mb-2">{translate(language, "PAGES.HOME.LATEST_DEMO.RESOURCES")}</div>
                <div className="d-flex flex-wrap gap-2">
                  {latestDemo.documents_link && (
                    <DstButton
                      value={translate(language, "PAGES.HOME.LATEST_DEMO.DOCUMENTS")}
                      variant="primary"
                      btnClass="m-regular"
                      target="_blank"
                      clickFunction={() => window.open(latestDemo.documents_link!, "_blank")}
                    />
                  )}
                  {latestDemo.record_link && (
                    <DstButton
                      value={translate(language, "PAGES.HOME.LATEST_DEMO.RECORD")}
                      variant="primary"
                      btnClass="m-regular"
                      target="_blank"
                      clickFunction={() => window.open(latestDemo.record_link!, "_blank")}
                    />
                  )}
                  {latestDemo.slide_link && (
                    <DstButton
                      value={translate(language, "PAGES.HOME.LATEST_DEMO.SLIDES")}
                      variant="primary"
                      btnClass="m-regular"
                      target="_blank"
                      clickFunction={() => window.open(latestDemo.slide_link!, "_blank")}
                    />
                  )}
                </div>
              </React.Fragment>
            ) : (
              <p className="text-muted fst-italic text-center m-0">
                {translate(language, "PAGES.HOME.LATEST_DEMO.NO_LINKS")}
              </p>
            )}
          </React.Fragment>
        )}
      </Card.Body>
      {loading ? (
        <Card.Footer className="d-flex justify-content-end p-3">
          <DstButton mock btnWidth="120px" />
        </Card.Footer>
      ) : (
        <Card.Footer className="d-flex justify-content-end p-3">
          <DstButton
            variant="secondary"
            value={translate(language, "PAGES.HOME.LATEST_DEMO.SEE_ALL_DEMOS")}
            clickFunction={() => navigate("/demo/records")}
            btnClass="m-regular"
          />
        </Card.Footer>
      )}
    </Card>
  );
};

export default LatestDemo;
